import { AxiosPromise, AxiosStatic } from "axios";
import {
  IfIsaAccount,
  CreateIfIsaAccountDto,
  UpdateIfIsaAccountDto,
  IfIsaYearlyContribution,
  CreateIfIsaTransferDto,
  IfIsaTransfer,
  UploadIfIsaTransferSignatureDto,
} from "../types/IfIsa";

export const getIfIsaAccount =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<IfIsaAccount | null> =>
    await appAxios.get(`${serverURL}/if-isa`, {
      withCredentials: true,
    });

export const createIfIsaAccount =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data: CreateIfIsaAccountDto): AxiosPromise<IfIsaAccount> =>
    await appAxios.post(`${serverURL}/if-isa`, data, {
      withCredentials: true,
    });

export const updateIfIsaAccount =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (id: string, data: UpdateIfIsaAccountDto): AxiosPromise<IfIsaAccount> =>
    await appAxios.put(`${serverURL}/if-isa/${id}`, data, {
      withCredentials: true,
    });

export const getYearlyContributionForCurrentYear =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<IfIsaYearlyContribution> =>
    await appAxios.get(`${serverURL}/if-isa/transactions/yearly`, {
      withCredentials: true,
    });

export const createIfIsaTransfer =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data: CreateIfIsaTransferDto): AxiosPromise<IfIsaTransfer> =>
    await appAxios.post(`${serverURL}/if-isa/transfers`, data, {
      withCredentials: true,
    });

export const uploadIfIsaTransferSignature =
  (serverURL: string, appAxios: AxiosStatic) =>
  async ({
    signatureFile,
    ifIsaTransferId,
  }: UploadIfIsaTransferSignatureDto): AxiosPromise<IfIsaTransfer> => {
    const isBase64DataUrl = (uri: string) => uri.startsWith("data:image/");

    const base64ToBlob = (dataUrl: string) => {
      const [header, base64Data] = dataUrl.split(",");
      const mimeMatch = header.match(/data:([^;]+)/);
      const mimeType = mimeMatch ? mimeMatch[1] : "image/png";
      const byteString = atob(base64Data);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);

      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }

      const WebBlob = (
        globalThis as unknown as {
          Blob: new (
            parts: ArrayBufferView[],
            options?: { type?: string }
          ) => Blob;
        }
      ).Blob;
      return new WebBlob([uint8Array], { type: mimeType });
    };

    const formData = new FormData();
    const fileName = signatureFile.name ?? "signed-transfer.png";
    const fileType = signatureFile.type ?? "image/png";

    if (isBase64DataUrl(signatureFile.uri)) {
      // Web browser: convert base64 data URL to Blob
      const blob = base64ToBlob(signatureFile.uri);
      (
        formData.append as (
          name: string,
          value: Blob,
          fileName?: string
        ) => void
      )("file", blob, fileName);
    } else {
      // React Native: use the uri object format
      formData.append("file", {
        uri: signatureFile.uri,
        name: fileName,
        type: fileType,
      } as any);
    }

    const result = await appAxios.post(
      `${serverURL}/if-isa/transfers/${ifIsaTransferId}/upload-signature`,
      formData,
      {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return result;
  };

export const getMyIfIsaTransfers =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<IfIsaTransfer[]> =>
    await appAxios.get(`${serverURL}/if-isa/transfers`, {
      withCredentials: true,
    });
